import { useState, useRef } from 'react';
import { Upload, FileText, X, Check, Loader } from 'lucide-react';
import { fileUploadService } from '../lib/fileUploadService';
import { useAuthStore } from '../store/authStore';

interface FileUploadProps {
  sessionId: string | null;
  onFileUploaded: (parsedContent?: any) => void;
}

export function FileUpload({ sessionId, onFileUploaded }: FileUploadProps) {
  const { user } = useAuthStore();
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = fileUploadService.validateFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setError(null);
    setUploadedFile(file);
  };

  const handleUpload = async () => {
    if (!uploadedFile || !sessionId || !user) return;

    setUploading(true);
    try {
      const result = await fileUploadService.uploadResume(user.id, uploadedFile, 'uploaded');

      if (result.success) {
        const parsedContent = await fileUploadService.parseResumeContent(uploadedFile);

        setUploading(false);
        setUploadedFile(null);
        onFileUploaded(parsedContent);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(result.error || 'Upload failed');
        setUploading(false);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed. Please try again.');
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setUploadedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="border-t-2 border-gray-200 p-4 bg-gray-50">
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileSelect}
          className="hidden"
          id="resume-file-upload"
        />

        {!uploadedFile ? (
          <label
            htmlFor="resume-file-upload"
            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-xl cursor-pointer hover:border-cyan-500 transition-all text-sm font-semibold text-gray-700 hover:text-cyan-700"
          >
            <Upload className="w-4 h-4" />
            Attach Resume
          </label>
        ) : (
          <div className="flex-1 flex items-center gap-3 bg-white border-2 border-cyan-500 rounded-xl px-4 py-2">
            <FileText className="w-5 h-5 text-cyan-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{uploadedFile.name}</p>
              <p className="text-xs text-gray-500">
                {(uploadedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <div className="flex items-center gap-2">
              {uploading ? (
                <Loader className="w-5 h-5 text-cyan-600 animate-spin" />
              ) : (
                <>
                  <button
                    onClick={handleUpload}
                    className="p-1.5 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                    disabled={!sessionId}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleRemove}
                    className="p-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-2">{error}</p>
      )}

      {!sessionId && uploadedFile && (
        <p className="text-xs text-gray-500 mt-2">
          Start a chat session first to upload files
        </p>
      )}
    </div>
  );
}
